import { useState, useCallback } from 'react';
import { ApiError } from '../lib/api';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useAsync<T, Args extends any[]>(
  fn: (...args: Args) => Promise<T>
): AsyncState<T> & { run: (...args: Args) => Promise<T | undefined>; reset: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: false, error: null });

  const run = useCallback(async (...args: Args) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const data = await fn(...args);
      setState({ data, loading: false, error: null });
      return data;
    } catch (err: any) {
      const msg = err instanceof ApiError ? err.message : (err?.message ?? 'Request failed');
      setState({ data: null, loading: false, error: msg });
      return undefined;
    }
  }, [fn]);

  const reset = useCallback(() => setState({ data: null, loading: false, error: null }), []);

  return { ...state, run, reset };
}
